import React from 'react';
import { Box, Typography } from '@mui/material';
import { processHtmlVariables, getAllVariables, sanitizeHtmlContent } from '../utils/htmlVariables';

interface HtmlContentFieldProps {
  field: {
    id: string;
    name: string;
    type: string;
    label?: string;
    htmlContent?: {
      content: string;
      enableVariables: boolean;
      systemVariables?: string[];
      fieldVariables?: string[];
    };
  };
  formData?: any;
  submissionData?: any;
  isPreview?: boolean;
}

const HtmlContentField: React.FC<HtmlContentFieldProps> = ({
  field,
  formData,
  submissionData,
  isPreview = false
}) => {
  if (field.type !== 'html' || !field.htmlContent) {
    return null;
  }

  const { content = '', enableVariables = false } = field.htmlContent;

  let processedContent = content;

  // Process variables if enabled
  if (enableVariables && content) {
    const variables = getAllVariables(formData, submissionData);
    processedContent = processHtmlVariables(content, variables);
  }

  // Sanitize HTML content for security
  const sanitizedContent = sanitizeHtmlContent(processedContent);

  // If content is empty, show placeholder in preview mode
  if (!sanitizedContent && isPreview) {
    return (
      <Box
        sx={{
          p: 2,
          border: '2px dashed #ccc',
          borderRadius: 1,
          backgroundColor: '#f9f9f9',
          textAlign: 'center'
        }}
      >
        <Typography color="textSecondary" variant="body2">
          HTML Content Field - Empty
        </Typography>
      </Box>
    );
  }

  // If content is empty and not preview, don't render anything
  if (!sanitizedContent && !isPreview) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        mb: 2
      }}
    >
      {/* Render HTML content */}
      <Box
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        sx={{
          '& p': {
            margin: '1em 0',
            '&:first-of-type': { marginTop: 0 },
            '&:last-of-type': { marginBottom: 0 }
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: '1em 0 0.5em 0',
            '&:first-of-type': { marginTop: 0 }
          },
          '& ul, & ol': {
            margin: '1em 0',
            paddingLeft: '2em'
          },
          '& blockquote': {
            margin: '1em 0',
            padding: '0.5em 1em',
            borderLeft: '4px solid #ccc',
            backgroundColor: '#f9f9f9'
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto'
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            margin: '1em 0'
          },
          '& th, & td': {
            border: '1px solid #ddd',
            padding: '8px',
            textAlign: 'left'
          },
          '& th': {
            backgroundColor: '#f2f2f2',
            fontWeight: 'bold'
          }
        }}
      />
    </Box>
  );
};

export default HtmlContentField;
